import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAxios } from "../../common_nextgen/axios/AxiosHelper";
import Form from "../../common_nextgen/common_components/form/Form";
import FunctionKeys from "../../common_nextgen/common_components/FunctionKeys/FunctionKeys";
import ConfirmationModal from "../../common_nextgen/common_components/Modal/ConfirmationModal";
import useCustomNavigate from "../../common_nextgen/common_components/Navigation/NextGenNavigation";
import { useToast } from "../../common_nextgen/hooks/ToastContext";
import {
  PRICE_SCHEDULE_MODES,
  ROUTE_PATHS,
} from "../../common_nextgen/utils/Constants";
import {
  isAddMode,
  isEditMode,
  isReadOnlyMode,
} from "../../common_nextgen/utils/UtilFunctions";
import { formFields } from "./ClientLimitsFormConfig";
import { useRBAC } from "../../common_nextgen/hooks/RBACContext";

const screenInfo = {
  ADD: ["Client Limits", "RCPS2001"],
  CHANGE: ["Client Limits", "RCPS2001"],
  VIEW: ["Client Limits", "RCPS2001"],
};

const ClientLimitsForm = ({ mode }) => {
  const {writeAccess} = useRBAC();
  const location = useLocation();
  const navigateToPage = useCustomNavigate();
  const [selectedOption, setSelectedOption] = useState("no");
  /* const stateVal = location.state;
  const [currentState = {}] = [...stateVal].reverse();
  const previousState = [...stateVal];
  previousState.splice(stateVal.length - 1, 1);
  console.log("current state in form", currentState, previousState);

  const {
    selectedPatients = [],
    previousScreen = ROUTE_PATHS.PATIENT_PAY_SCHEDULE,
    currentIndex: initialIndex = 0,
    paramsData: initialParamsData = {
      ps3PatientPaySchName: selectedPatients[initialIndex].ps3PatientPaySchName,
      ps3PatientPaySchDesc: selectedPatients[initialIndex].ps3PatientPaySchDesc,
    },
  } = currentState;*/

  const [stateVal, setStateVal] = useState(location.state || []);
  const { currentState, previousState } = useMemo(() => {
    let [currentState = {}] = [...stateVal].reverse();
    const previousState = [...stateVal];
    previousState.splice(stateVal.length - 1, 1);
    return {
      previousState,
      currentState,
    };
  }, [stateVal]);

  const {
    selectedPatients = [],
    previousScreen = ROUTE_PATHS.PATIENT_PAY_SCHEDULE,
    currentIndex: initialIndex = 0,
    externalMode: initialExternalMode = false,
    currentMode: initialCurrentMode = mode
  } = useMemo(() => {
    const { selectedPatients, previousScreen, currentIndex, externalMode, currentMode } =
      currentState;
    return {
      selectedPatients,
      previousScreen,
      currentIndex,
      externalMode,
      currentMode
    };
  }, [currentState]);

  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [currentMode, setCurrentMode] = useState(initialCurrentMode);
  const [initialFormData, setInitialFormData] = useState({});
  const [dataForSubmission, setDataForSubmission] = useState({});
  const [constraintErrors, setConstraintErrors] = useState([]);
  const toast = useToast();
  const { axios_helper_no_header } = useAxios();
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [externalMode, setExternalMode] = useState(initialExternalMode);

  const handleBack = () => {
    if (externalMode) {
      const message = {
        message: "navigate-to-external-program",
        url: previousScreen,
        parameters: {
          state: [...previousState],
        },
      };
      console.log("back messages", message);
      window.parent.postMessage(message, "*");
    } else if (currentIndex < selectedPatients.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setCurrentMode(mode);
    } else {
      navigateToPage(previousScreen, previousState);
    }
  };

  const openConfirmationModal = () => setIsConfirmationModalOpen(true);
  const closeConfirmationModal = () => setIsConfirmationModalOpen(false);
  const openDeleteModal = () => setIsDeleteModalOpen(true);
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedOption("no");
  };

  const getPageTitle = useCallback(() => {
    const [screenTitle, screenNumber] = isAddMode(currentMode)
      ? screenInfo.ADD
      : screenInfo.CHANGE;
    return (
      <>
        <span className="title">{screenTitle}</span>
        <span>{"-"}</span>
        <span className="screen-number">{screenNumber}</span>
      </>
    );
  }, [currentMode]);

  const submitForm = async (dataForSubmission) => {
    let url = "";
    let verb = "";
    let screenNumber = "";

    if (isAddMode(currentMode)) {
      url = "api/ps2patpayschclntlmts";
      verb = "PUT";
      screenNumber = screenInfo.ADD[1];
    } else {
      url = "api/ps2patpayschclntlmts";
      verb = "PUT";
      screenNumber = screenInfo.CHANGE[1];

      if (
        JSON.stringify(dataForSubmission) === JSON.stringify(initialFormData)
      ) {
        toast.current.show({
          severity: "info",
          summary: "",
          detail: "No Change of Information",
          life: 3000,
        });
        return;
      }
    }

    const data = { ...dataForSubmission, screenNumber };

    try {
      const { responseStatus, responseData } = await axios_helper_no_header(
        verb,
        url,
        data,
        null
      );
      toast.current.show({
        severity: "success",
        summary: "",
        detail: responseStatus.message,
        life: 3000,
      });
      setInitialFormData(responseData);
      if (isAddMode(currentMode)) {
        setCurrentMode(writeAccess ? PRICE_SCHEDULE_MODES.EDIT : PRICE_SCHEDULE_MODES.READ_ONLY);
      }
    } catch (error) {
      const {
        responseStatus: { displayErrorFields = null, message },
      } = error.data;
      if (displayErrorFields === null) {
        toast.current.show({
          severity: "error",
          summary: "",
          detail: message,
          life: 3000,
        });
      } else {
        setConstraintErrors(displayErrorFields);
      }
    }
  };

  const [keyFunc, keyName] = useMemo(() => {
    const keyFunc = {};
    const keyName = {};

    if (isAddMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
    } else if (isEditMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
      keyFunc.chmod = () => setCurrentMode(PRICE_SCHEDULE_MODES.READ_ONLY);
      keyName.chmod = "View";
      keyFunc.F11 = () => {
        openDeleteModal();
        if (isDeleteModalOpen) {
          deleteEntity();
        }
      };
      keyName.F11 = "Delete";
    } else if (isReadOnlyMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
      keyFunc.chmod = () => setCurrentMode(PRICE_SCHEDULE_MODES.EDIT);
      keyName.chmod = "Edit";
    }

    return [keyFunc, keyName];
  }, [currentMode, currentIndex, externalMode]);

  const params = {
    ps3PatientPaySchName: selectedPatients[currentIndex]?.ps3PatientPaySchName,
    ps3PatientPaySchDesc: selectedPatients[currentIndex]?.ps3PatientPaySchDesc,
  };

  const deleteEntity = async () => {
    if (selectedOption === "yes") {
      const url = `api/ps2patpayschclntlmts?ps3PatientPaySchName=${encodeURIComponent(
        selectedPatients[currentIndex].ps3PatientPaySchName
      )}`;

      try {
        const { responseStatus } = await axios_helper_no_header("DELETE", url);
        toast.current.show({
          severity: "success",
          summary: "",
          detail: responseStatus.message,
          life: 3000,
        });
        setCurrentMode(PRICE_SCHEDULE_MODES.ADD);

        const fetchInitialData = async () => {
          const url = `api/ps2patpayschclntlmts/initialFormData/${encodeURIComponent(
            screenInfo.ADD[1]
          )}`;
          const { responseData } = await axios_helper_no_header(
            "GET",
            url,
            null,
            params
          );
          setInitialFormData(responseData);
        };

        // fetchInitialData(); // Refresh the data
        handleBack();
      } catch (error) {
        const { responseStatus: { message } = {} } = error.data;
        toast.current.show({
          severity: "error",
          summary: "",
          detail: message,
          life: 3000,
        });
      } finally {
        setIsConfirmationModalOpen(false);
        closeDeleteModal();
      }
    } else {
      closeDeleteModal();
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      const url = isAddMode(currentMode)
        ? `api/ps2patpayschclntlmts/initialFormData/${encodeURIComponent(
            screenInfo.ADD[1]
          )}`
        : `api/ps2patpayschclntlmts/initialFormData/${encodeURIComponent(
            screenInfo.ADD[1]
          )}`;
      const { responseData } = await axios_helper_no_header(
        "GET",
        url,
        null,
        params
      );
      setInitialFormData(responseData);
      const { addDate } = responseData;
      if (addDate) {
        setCurrentMode(writeAccess ? PRICE_SCHEDULE_MODES.EDIT : PRICE_SCHEDULE_MODES.READ_ONLY);
      }
    };

    if (selectedPatients.length) fetchInitialData();
  }, [mode, currentIndex, externalMode]);

  const handleMessage = (event) => {
    const { type, data } = event.data;

    if (type === "set-params") {
      setStateVal(data);
      setExternalMode(true);
    }
    console.log("message", event.data);
  };

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  });

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);
  useEffect(() => {
    setExternalMode(initialExternalMode);
  }, [initialExternalMode]);

  return (
    <div className="grid container">
      <div className="col col-12">
        <FunctionKeys keyFunc={keyFunc} keyName={keyName} />
      </div>
      <div className="col col-12">
        <h1>{getPageTitle()}</h1>
      </div>
      <div className="col col-12">
        <Form
          formData={initialFormData}
          formFields={formFields}
          mode={currentMode}
          submitFormData={submitForm}
          constraintErrors={constraintErrors}
        />
      </div>
      <div className="col col-12">
        {isConfirmationModalOpen && (
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            onRequestClose={closeConfirmationModal}
            onBack={closeConfirmationModal}
            onSave={submitForm}
            confirmLabel="Confirm(Y/N)"
            options={[
              { label: "N-No", value: "no" },
              { label: "Y-Yes", value: "yes" },
            ]}
            setSelectedOption={setSelectedOption}
          />
        )}
        {isDeleteModalOpen && (
          <ConfirmationModal
            isOpen={isDeleteModalOpen}
            onRequestClose={closeDeleteModal}
            onBack={closeDeleteModal}
            onSave={deleteEntity}
            confirmLabel="Confirm Delete(Y/N)"
            options={[
              { label: "N-No", value: "no" },
              { label: "Y-Yes", value: "yes" },
            ]}
            setSelectedOption={setSelectedOption}
          />
        )}
      </div>
    </div>
  );
};

export default ClientLimitsForm;
